/**
 * useSubscription — fetch and cache the current user's payments-infra subscription.
 *
 * Returns tier, status, limits, usage, and portal/billing helpers.
 * Fetches once on mount when userId is available; re-fetches on userId change.
 */
import { useState, useEffect, useCallback } from 'react'
import { getSubscription, openBillingPortal, SubscriptionResult } from '@/lib/payments'

interface UseSubscriptionReturn {
  subscription:    SubscriptionResult | null
  loading:         boolean
  error:           string | null
  tier:            'free' | 'pro' | 'enterprise'
  isPro:           boolean
  isEnterprise:    boolean
  refetch:         () => void
  openPortal:      () => Promise<void>
  portalLoading:   boolean
}

export function useSubscription(userId: string | undefined): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<SubscriptionResult | null>(null)
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)

  const fetchSubscription = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const result = await getSubscription(userId)
      setSubscription(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  const openPortal = useCallback(async () => {
    if (!userId) return
    setPortalLoading(true)
    try {
      await openBillingPortal(userId)
    } catch (err) {
      console.error('[useSubscription] portal error:', err)
    } finally {
      setPortalLoading(false)
    }
  }, [userId])

  const tier = subscription?.tier ?? 'free'

  return {
    subscription,
    loading,
    error,
    tier,
    isPro:         tier === 'pro' || tier === 'enterprise',
    isEnterprise:  tier === 'enterprise',
    refetch:       fetchSubscription,
    openPortal,
    portalLoading,
  }
}
